import { useBuilderContext } from "@Context/game/builder/builder-context";
import useI18n from "@i18n";
import { Button, FormControlLabel, Radio, RadioGroup, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { BuilderDialog } from "./builder-dialog";

type FixOption = "time" | "height";

const PhysicsPromptDialog = () => {
  const { state, dispatch } = useBuilderContext();
  const { i18n } = useI18n();
  const theme = useTheme();
  const prompt = state.physicsPrompt;
  const [fixOption, setFixOption] = useState<FixOption>("time");

  if (!prompt) return null;

  const handleClose = () => {
    prompt.onCancel();
    dispatch({ physicsPrompt: null });
  };

  const handleConfirm = () => {
    prompt.onConfirm();
    dispatch({ physicsPrompt: null });
  };

  const handleFixConfirm = () => {
    if (fixOption === "time") {
      prompt.onConfirmAdjustTime?.();
    } else {
      prompt.onConfirmAdjustHeight?.();
    }
    dispatch({ physicsPrompt: null });
  };

  const bodyStyle = { color: theme.palette.dsBase.v200 };

  if (prompt.mode === "height-changed") {
    return (
      <BuilderDialog
        name="physics-prompt"
        title={i18n("builder.physics.title.adjust.flight.time")}
        open
        onClose={handleClose}
        dialogActions={
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleClose} aria-label={i18n("label.cancel")}>
              {i18n("label.cancel")}
            </Button>
            <Button variant="contained" onClick={handleConfirm} aria-label={i18n("button.confirm")}>
              {i18n("button.confirm")}
            </Button>
          </Stack>
        }
      >
        <Typography variant="body1" sx={bodyStyle}>
          {i18n("builder.physics.height.changed.description", {
            height: prompt.proposedHeightName,
            meters: prompt.proposedHeight.toFixed(1),
            requiredTime: prompt.proposedFlightTime.toFixed(2),
            currentTime: prompt.currentFlightTime.toFixed(2),
          })}
        </Typography>
        <Typography variant="body1" sx={{ ...bodyStyle, mt: 1 }}>
          {i18n("builder.physics.height.changed.note")}
        </Typography>
      </BuilderDialog>
    );
  }

  if (prompt.mode === "time-changed") {
    return (
      <BuilderDialog
        name="physics-prompt"
        title={i18n("builder.physics.title.adjust.height")}
        open
        onClose={handleClose}
        dialogActions={
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleClose} aria-label={i18n("label.cancel")}>
              {i18n("label.cancel")}
            </Button>
            <Button variant="contained" onClick={handleConfirm} aria-label={i18n("button.confirm")}>
              {i18n("button.confirm")}
            </Button>
          </Stack>
        }
      >
        <Typography variant="body1" sx={bodyStyle}>
          {i18n("builder.physics.time.changed.description", {
            currentTime: prompt.currentFlightTime.toFixed(2),
            height: prompt.proposedHeightName,
            meters: prompt.proposedHeight.toFixed(1),
          })}
        </Typography>
        <Typography variant="body1" sx={{ ...bodyStyle, mt: 1 }}>
          {i18n("builder.physics.time.changed.note", {
            fromHeight: prompt.currentHeightName,
            toHeight: prompt.proposedHeightName,
          })}
        </Typography>
      </BuilderDialog>
    );
  }

  if (prompt.mode === "fix") {
    return (
      <BuilderDialog
        name="physics-prompt"
        title={i18n("builder.physics.title.fix")}
        open
        onClose={handleClose}
        dialogActions={
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleClose} aria-label={i18n("label.cancel")}>
              {i18n("label.cancel")}
            </Button>
            <Button variant="contained" onClick={handleFixConfirm} aria-label={i18n("button.confirm")}>
              {i18n("button.confirm")}
            </Button>
          </Stack>
        }
      >
        <Typography variant="body1" sx={bodyStyle}>
          {i18n("builder.physics.fix.description", {
            height: prompt.currentHeightName,
            meters: prompt.currentHeight.toFixed(1),
            requiredTime: prompt.proposedFlightTime.toFixed(2),
            currentTime: prompt.currentFlightTime.toFixed(2),
          })}
        </Typography>
        <RadioGroup
          value={fixOption}
          onChange={(e) => setFixOption(e.target.value as FixOption)}
          sx={{ mt: 2 }}
        >
          <FormControlLabel
            value="time"
            control={<Radio />}
            label={i18n("builder.physics.fix.option.time", {
              currentTime: prompt.currentFlightTime.toFixed(2),
              time: prompt.proposedFlightTime.toFixed(2),
              height: prompt.currentHeightName,
            })}
            sx={{ color: theme.palette.dsBase.v200 }}
          />
          <FormControlLabel
            value="height"
            control={<Radio />}
            label={i18n("builder.physics.fix.option.height", {
              currentHeight: prompt.currentHeightName,
              currentMeters: prompt.currentHeight.toFixed(1),
              height: prompt.proposedHeightName,
              meters: prompt.proposedHeight.toFixed(1),
            })}
            sx={{ color: theme.palette.dsBase.v200 }}
          />
        </RadioGroup>
      </BuilderDialog>
    );
  }

  return null;
};

export default PhysicsPromptDialog;
